import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import SEO from '../components/SEO';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Footer from '../components/Footer';
import DemoRequestModal from '../components/DemoRequestModal';
import StickyCTABar from '../components/StickyCTABar';
import MiniScanWidget from '../components/MiniScanWidget';
import PublicActivityFeed from '../components/PublicActivityFeed';
import BetaCountdown from '../components/BetaCountdown';
import ExitIntentPopup from '../components/ExitIntentPopup';
import PressBanner from '../components/PressBanner';
import IndexHighlights from '../components/IndexHighlights';
import { publicApi } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { PLANS, START_PRICE, PROFESSIONAL_PRICE, formatRon } from '../config/pricing';
import { captureUtmParams, isPressVisit } from '../utils/trafficSource';

// Cardul evidentiat = cel mai scump pachet cumparabil self-serve din tabela.
// Derivat, nu scris: daca grila se schimba, se muta si accentul vizual.
const FEATURED_TEASER_PLAN_ID = PLANS.filter((p) => p.checkout)
  .reduce((a, b) => (a && a.amount_excl_vat > b.amount_excl_vat ? a : b), null)?.id || null;

/**
 * Cardurile din teaser-ul de preturi = TOATE pachetele din tabela, in ordinea ei.
 *
 * Varianta veche lua `tiers[0]`, `tiers[1]`, `tiers[2]` din i18n; in clipa in care
 * grila a trecut la patru pachete, homepage-ul sarea peste unul dintre ele (Start)
 * si trimitea vizitatorii direct de la Gratuit la cel mai scump pachet lunar.
 *
 * Textele raman in i18n (alt proprietar), cautate dupa cheia pachetului. Cand o
 * limba n-are inca `key` in traduceri, cadem pe pozitie DOAR daca traducerea are
 * exact cate o intrare per pachet: altfel s-ar lipi numele unui pachet de pretul
 * altuia (o traducere ramasa pe grila veche ar scrie "Professional" peste pretul
 * pachetului Start). Cifrele vin exclusiv din tabela.
 */
function buildPricingTeaser(t) {
  const raw = t('landing.pricing_preview.tiers', { returnObjects: true });
  const copy = Array.isArray(raw) ? raw : [];
  const byKey = new Map(copy.filter((c) => c && c.key).map((c) => [c.key, c]));
  const positionalOk = copy.length === PLANS.length;
  return PLANS.map((plan, i) => {
    const c = byKey.get(plan.id) || (positionalOk ? copy[i] : null) || {};
    const hasPrice = plan.amount_excl_vat !== null && plan.amount_excl_vat !== undefined;
    // Pachetul fara pret public se vinde prin discutie, nu prin checkout.
    const onDemo = !plan.checkout && !hasPrice;
    return {
      name: c.name || plan.name,
      price: onDemo ? (c.price || plan.display_price) : plan.display_excl_vat_short.replace(' RON', ''),
      unit: onDemo ? '' : (plan.amount_excl_vat === 0 ? (c.unit || plan.currency) : `${plan.currency} ${c.unit_suffix || ''}`),
      desc: c.desc || '',
      features: Array.isArray(c.features) ? c.features : [],
      // Fara text tradus, trimitem la pagina de preturi cu eticheta ei — mai bine
      // un buton onest decat unul gol.
      cta: c.cta || t('landing.pricing_preview.see_all_link'),
      ctaLink: onDemo ? null : (c.cta ? '/register' : '/preturi'),
      highlight: plan.id === FEATURED_TEASER_PLAN_ID,
      onDemo,
    };
  });
}

// Construieste breadcrumb-ul JSON-LD pe baza limbii curente.
function buildLandingBreadcrumb(t) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('landing.json_ld.breadcrumb_home'), item: 'https://www.privatebrands.ro/' },
    ],
  };
}

// Construieste schema FAQ JSON-LD pe baza limbii curente.
// Q1-Q4: intrebari despre platforma. Q5-Q10: intrebari AEO-target formulate
// exact cum o persoana le-ar tasta in ChatGPT/Perplexity (intercepteaza
// cautari reale, nu doar branded queries).
function buildLandingFaq(t) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: t('landing.json_ld.faq.q1'), acceptedAnswer: { '@type': 'Answer', text: t('landing.json_ld.faq.a1') } },
      { '@type': 'Question', name: t('landing.json_ld.faq.q2'), acceptedAnswer: { '@type': 'Answer', text: t('landing.json_ld.faq.a2') } },
      { '@type': 'Question', name: t('landing.json_ld.faq.q3'), acceptedAnswer: { '@type': 'Answer', text: t('landing.json_ld.faq.a3') } },
      { '@type': 'Question', name: t('landing.json_ld.faq.q4'), acceptedAnswer: { '@type': 'Answer', text: t('landing.json_ld.faq.a4') } },
      { '@type': 'Question', name: t('landing.json_ld.faq.q5'), acceptedAnswer: { '@type': 'Answer', text: t('landing.json_ld.faq.a5') } },
      { '@type': 'Question', name: t('landing.json_ld.faq.q6'), acceptedAnswer: { '@type': 'Answer', text: t('landing.json_ld.faq.a6') } },
      { '@type': 'Question', name: t('landing.json_ld.faq.q7'), acceptedAnswer: { '@type': 'Answer', text: t('landing.json_ld.faq.a7') } },
      { '@type': 'Question', name: t('landing.json_ld.faq.q8'), acceptedAnswer: { '@type': 'Answer', text: t('landing.json_ld.faq.a8') } },
      { '@type': 'Question', name: t('landing.json_ld.faq.q9'), acceptedAnswer: { '@type': 'Answer', text: t('landing.json_ld.faq.a9') } },
      { '@type': 'Question', name: t('landing.json_ld.faq.q10'), acceptedAnswer: { '@type': 'Answer', text: t('landing.json_ld.faq.a10') } },
    ],
  };
}

function HeroSearch({ onScanRequest }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [allBrands, setAllBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    publicApi.brands().then(res => {
      if (!cancelled) { setAllBrands(res.data.brands || []); setLoading(false); }
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || q.length < 2) return [];
    return allBrands.filter(b => (b.name || '').toLowerCase().includes(q)).slice(0, 6);
  }, [query, allBrands]);

  const goToFirst = (e) => {
    if (e) e.preventDefault();
    if (matches.length > 0) {
      const b = matches[0];
      navigate(`/branduri/${b.industry}/${b.slug}`);
    } else if (query.trim().length >= 2) {
      // Brand negasit in index → mini-scan gratuit pe loc (hero), nu redirect
      // la register: valoarea intai, contul dupa.
      onScanRequest(query.trim());
    }
  };

  return (
    <form onSubmit={goToFirst} className="hero-search-form" style={{ maxWidth: 640, margin: '0 auto 32px', position: 'relative' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        <div className="hero-search-row" style={{ display: 'flex', gap: 8, padding: 8, background: '#fff', borderRadius: 14, boxShadow: '0 18px 40px rgba(15,23,42,0.12)', border: '1px solid #E5E7EB' }}>
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={loading ? t('landing.hero_search.loading_placeholder') : t('landing.hero_search.placeholder')}
            className="hero-search-input"
            style={{
              flex: 1, minWidth: 0, padding: '14px 16px', border: 'none', outline: 'none',
              fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, color: '#0A1428',
              background: 'transparent',
            }}
            autoComplete="off"
          />
          <button type="submit" className="hero-search-btn" style={{
            padding: '14px 28px', background: '#0A1428', color: '#fff', border: 'none', borderRadius: 8,
            fontFamily: 'DM Mono, monospace', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 700, cursor: 'pointer',
            whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            {t('landing.hero_search.button')}
          </button>
        </div>

        {matches.length > 0 && (
          <div style={{ background: '#fff', marginTop: 8, borderRadius: 12, boxShadow: '0 18px 40px rgba(15,23,42,0.1)', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
            {matches.map(b => (
              <Link
                key={b.slug}
                to={`/branduri/${b.industry}/${b.slug}`}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '14px 18px', textDecoration: 'none', color: '#0A1428',
                  borderBottom: '1px solid #F3F4F6',
                  fontFamily: 'Cormorant Garamond, serif', fontSize: 17,
                }}
              >
                <span>{b.name}</span>
                <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: '#4B5563', textTransform: 'uppercase', letterSpacing: 1 }}>
                  {b.industry_label}
                </span>
              </Link>
            ))}
          </div>
        )}

        {query.trim().length >= 2 && matches.length === 0 && !loading && (
          <div style={{ marginTop: 8, padding: '16px 18px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#92400E' }}>
            <span style={{ display: 'block', marginBottom: 10 }}>{t('landing.hero_search.not_found_text')}</span>
            <button
              type="button"
              onClick={() => onScanRequest(query.trim())}
              style={{
                padding: '10px 20px', background: '#0A1428', color: '#FFFFFF', border: 'none',
                borderRadius: 8, fontFamily: 'DM Mono, monospace', fontSize: 12, letterSpacing: 1,
                fontWeight: 700, cursor: 'pointer',
              }}
            >
              {t('landing.hero_search.not_found_scan_button', { query: query.trim() })}
            </button>
          </div>
        )}
      </div>
    </form>
  );
}

export default function LandingPage() {
  const { t } = useTranslation();
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [demoSource, setDemoSource] = useState('unknown');
  const { user } = useAuth();

  // Atribuire UTM inainte de primul render, ca gating-ul de presa sa fie
  // corect din prima (un useEffect ar rula abia dupa ce popup-urile s-au montat).
  // Pe traficul venit din articole de presa NU afisam pattern-uri de urgenta
  // (exit-intent, countdown) — cel mai sceptic segment de vizitatori.
  const [pressVisit] = useState(() => { captureUtmParams(); return isPressVisit(); });

  // Prefill pentru mini-scanul din hero cand cautarea secundara nu gaseste
  // brandul. Obiect nou la fiecare click ca effect-ul din widget sa se
  // redeclanseze si pagina sa deruleze inapoi la scanner.
  const [miniScanPrefill, setMiniScanPrefill] = useState(null);
  const handleScanRequest = (name) => {
    setMiniScanPrefill({ name });
    document.getElementById('mini-scan-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Cifrele din banda de social proof erau hardcodate ("147 scanari AI azi",
  // "150+ branduri din 11 industrii"). Acum vin din /api/public/stats; daca
  // apelul esueaza, banda se ascunde. Mai bine nimic decat un numar inventat.
  const [stats, setStats] = useState(null);
  useEffect(() => {
    publicApi.stats()
      .then(r => setStats(r.data?.available ? r.data : null))
      .catch(() => setStats(null));
  }, []);

  const openDemo = (source) => {
    setDemoSource(source);
    setDemoModalOpen(true);
  };

  const landingBreadcrumb = buildLandingBreadcrumb(t);
  const landingFaq = buildLandingFaq(t);

  // Economia lunara din tabelul de comparatie: costul monitorizarii manuale (singura
  // cifra care ramane in traducere, fiind o estimare de piata, nu un pret al nostru)
  // minus pretul real al pachetului. Extragem doar cifrele, ca sa mearga si cu
  // separatorul RO ("4.000 RON") si cu cel EN ("4,000 RON").
  const monthlySavings = useMemo(() => {
    const manual = parseInt(String(t('landing.comparison_table.manual_cost_value')).replace(/\D/g, ''), 10);
    const diff = manual - PROFESSIONAL_PRICE.amount_excl_vat;
    if (!Number.isFinite(diff) || diff <= 0) return '';
    return `${formatRon(diff)} RON`;
  }, [t]);

  return (
    <div className="page-shell landing-page">
      <SEO
        title={t('landing.seo.title')}
        description={t('landing.seo.description')}
        path="/"
        jsonLd={[landingBreadcrumb, landingFaq]}
      />

      <DemoRequestModal open={demoModalOpen} onClose={() => setDemoModalOpen(false)} source={demoSource} />
      {/* Fara pattern-uri de urgenta pe traficul venit din articole de presa */}
      {!pressVisit && <ExitIntentPopup onDemoClick={() => openDemo('exit-intent')} />}
      <StickyCTABar onDemoClick={() => openDemo('sticky-bar')} />

      {/* HEADER */}
      <header className="page-header" style={{ background: '#ffffff', padding: '24px 48px', borderRadius: 28, boxShadow: '0 18px 50px rgba(15,23,42,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 18px', background: '#ffffff', borderRadius: 18, border: '1px solid rgba(229,231,235,0.9)', boxShadow: '0 12px 30px rgba(15,23,42,0.08)' }}>
          <img src="/logo.jpg" alt="Private Brands" width="76" height="52" style={{ height: '52px', width: 'auto', objectFit: 'contain' }} />
        </div>
        <nav className="nav-links">
          <Link to="/branduri">{t('landing.header.nav_branduri')}</Link>
          <Link to="/metodologie">{t('landing.header.nav_metodologie')}</Link>
          <Link to="/banking">{t('landing.header.nav_banking')}</Link>
          <Link
            to={user ? '/dashboard' : '/login'}
            className="nav-login-btn"
            aria-label={user ? t('landing.header.aria_dashboard') : t('landing.header.aria_login')}
            style={{
              background: '#0A1428',
              color: '#FFFFFF',
              border: '1.5px solid #0A1428',
              padding: '8px 18px',
              borderRadius: 999,
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: 0.2,
              textDecoration: 'none',
              transition: 'all 0.25s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 2px 8px rgba(10,20,40,0.18)',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#1f2937';
              e.currentTarget.style.borderColor = '#D4AF37';
              e.currentTarget.style.boxShadow = '0 4px 14px rgba(10,20,40,0.28)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#0A1428';
              e.currentTarget.style.borderColor = '#0A1428';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(10,20,40,0.18)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 13, color: '#8B6914' }}>{user ? '◉' : '→'}</span>
            {user ? t('landing.header.dashboard') : t('landing.header.login')}
          </Link>
          <button
            type="button"
            onClick={() => openDemo('header-cta')}
            className="nav-demo-btn"
            style={{
              background: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)',
              color: '#0A1428',
              border: '1.5px solid #D4AF37',
              padding: '8px 18px',
              borderRadius: 999,
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: 0.2,
              cursor: 'pointer',
              transition: 'all 0.25s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 2px 8px rgba(212,175,55,0.15)',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #D4AF37 0%, #B8972E 100%)';
              e.currentTarget.style.color = '#0A1428';
              e.currentTarget.style.boxShadow = '0 4px 14px rgba(212,175,55,0.35)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)';
              e.currentTarget.style.color = '#0A1428';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(212,175,55,0.15)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <span style={{ fontSize: 14 }}>✦</span>
            {t('landing.header.request_demo')}
          </button>
          {/* 2026-05-15 — Language switcher RO/EN pe LandingPage header */}
          <LanguageSwitcher compact />
        </nav>
      </header>

      <main>
      {/* HERO — mini-scanul E pagina. O singura actiune deasupra foldului.
          Fostul split layout (text + mock dashboard) a disparut: mock-ul afisa
          un scor inventat (BCR 47/100) pe indicii legacy, retrasi
          din UI-ul de banking in favoarea celor 6 axe RRI — deci vindea un ecran
          care nu mai exista. Cautarea in branduri si butonul de demo au coborat
          sub fold: doua campuri de input deasupra foldului inseamna doua
          actiuni concurente, deci niciuna. */}
      <section id="mini-scan-section" style={{ padding: 'clamp(48px, 10vw, 96px) clamp(16px, 5vw, 48px) clamp(40px, 8vw, 64px)', background: 'linear-gradient(135deg, #F9FAFB 0%, #FFFFFF 100%)' }}>
        <div className="hero-grid" style={{
          maxWidth: '760px',
          margin: '0 auto',
          display: 'block',
          textAlign: 'center',
        }}>
          <div className="hero-text">
            <p style={{ fontSize: '13px', fontWeight: '600', color: '#8B6914', textTransform: 'uppercase', letterSpacing: '3px', marginBottom: '16px' }}>
              {t('landing.hero.eyebrow')}
            </p>

            <h1 style={{ fontSize: 'clamp(28px, 5vw, 52px)', fontWeight: '700', marginBottom: '20px', lineHeight: '1.1', color: '#0A1428', fontFamily: 'Playfair Display, serif' }}>
              {t('landing.hero.title_pre')}<span style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #B8972E 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{t('landing.hero.title_brand')}</span>{t('landing.hero.title_post')}
            </h1>

            <p style={{ fontSize: 'clamp(15px, 1.5vw, 18px)', color: '#4B5563', marginBottom: '32px', lineHeight: '1.6', fontFamily: 'Inter, system-ui, sans-serif' }}>
              <strong style={{ color: '#0A1428' }}>{t('landing.hero.subtitle_role')}</strong> {t('landing.hero.subtitle')}
            </p>

            <MiniScanWidget variant="hero" prefill={miniScanPrefill} />

            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', marginTop: 24, fontSize: '13px', color: '#6B7280', fontFamily: 'DM Mono, monospace', letterSpacing: 0.5 }}>
              <span>{t('landing.hero.trust_1')}</span>
              <span>{t('landing.hero.trust_2')}</span>
              <span>{t('landing.hero.trust_3')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* SECUNDAR — cautarea in brandurile deja masurate + demo. Coborate din
          hero ca sa nu concureze cu mini-scanul, dar pastrate imediat sub el:
          cine stie deja ce cauta n-are de derulat, iar funnel-ul de directory
          (SEO) isi pastreaza intrarea. */}
      <section style={{ padding: 'clamp(28px, 4vw, 40px) clamp(16px, 5vw, 48px)', background: '#FFFFFF', borderTop: '1px solid #F3F4F6' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto', textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: '#6B7280', fontFamily: 'Inter, system-ui, sans-serif', marginBottom: 16 }}>
            {t('landing.hero_search.secondary_intro')}
          </p>

          <HeroSearch onScanRequest={handleScanRequest} />

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => openDemo('hero-cta')}
              style={{
                padding: '12px 24px',
                background: 'transparent',
                color: '#0A1428',
                border: '2px solid #0A1428',
                borderRadius: 10,
                fontFamily: 'DM Mono, monospace',
                fontSize: 12,
                letterSpacing: 1.5,
                fontWeight: 700,
                textTransform: 'uppercase',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#0A1428'; e.currentTarget.style.color = '#FFFFFF'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#0A1428'; }}
            >
              {t('landing.hero.demo_button')}
            </button>
            <span style={{ fontSize: 12, color: '#6B7280', fontFamily: 'Inter, system-ui, sans-serif' }}>{t('landing.hero.demo_subtitle')}</span>
          </div>
        </div>
      </section>

      {/* LIVE COUNTER — social proof + urgenta */}
      <section style={{ padding: 'clamp(20px, 3vw, 28px) clamp(16px, 5vw, 48px)', background: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)', borderTop: '1px solid rgba(212,175,55,0.2)', borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
        <div style={{ maxWidth: '1180px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', gap: 'clamp(20px, 4vw, 48px)', justifyContent: 'center', alignItems: 'center' }}>
          {/* Fara punct verde pulsand: cifra e un agregat cache-uit 10 minute,
              nu un flux in timp real. Un indicator "live" fals e exact tiparul
              pe care cumparatorul B2B il detecteaza si dupa care nu mai crede
              nici restul paginii. */}
          {stats?.brands_with_rri > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 14, color: '#92400E', fontFamily: 'DM Mono, monospace', fontWeight: 600 }}>
                <strong style={{ color: '#0A1428', fontSize: 16 }}>{stats.brands_with_rri}</strong> {t('landing.live_counter.brands_with_rri_suffix')}
                {stats.industries_ranked > 0 && ` · ${t('landing.live_counter.industries_ranked', { n: stats.industries_ranked })}`}
              </span>
            </div>
          )}
          <div style={{ width: 1, height: 24, background: 'rgba(212,175,55,0.3)', display: 'none' }} className="counter-divider" />
          {!pressVisit && <BetaCountdown />}
          <div style={{ fontSize: 14, color: '#92400E', fontFamily: 'DM Mono, monospace' }}>
            {t('landing.live_counter.made_in_romania')}
          </div>
        </div>
      </section>

      {/* PRESA — apare automat cand config/press.js are mentiuni reale */}
      <PressBanner />

      {/* LOGOS STRIP — social proof */}
      <section style={{ padding: 'clamp(32px, 5vw, 48px) clamp(16px, 5vw, 48px)', background: '#FFFFFF', borderTop: '1px solid #F3F4F6', borderBottom: '1px solid #F3F4F6' }}>
        <div style={{ maxWidth: '1180px', margin: '0 auto', textAlign: 'center' }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 3, marginBottom: 24, fontFamily: 'DM Mono, monospace' }}>
            {stats
              ? t('landing.logos_strip.eyebrow_real', { brands: stats.brands_tracked, industries: stats.industries_tracked })
              : t('landing.logos_strip.eyebrow_fallback')}
          </p>
          {/* Toate numele de mai jos sunt in MARKET_PLAYERS, deci chiar sunt
              urmarite. "Auchan" a fost scos: nu figura in niciun registru. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center', alignItems: 'center' }}>
            {['BCR', 'Banca Transilvania', 'BRD', 'eMAG', 'Carrefour', 'Lidl', 'Orange', 'Vodafone', 'Dacia', 'BMW Romania', 'Medlife', 'Booking.com', 'Revolut', 'Telekom', 'Dedeman', 'Regina Maria'].map(brand => (
              <span key={brand} style={{
                padding: '8px 16px',
                background: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: 999,
                fontSize: 13,
                color: '#0A1428',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontWeight: 500,
              }}>
                {brand}
              </span>
            ))}
            <Link to="/branduri" style={{
              padding: '8px 16px',
              background: 'transparent',
              border: '1px solid #D4AF37',
              borderRadius: 999,
              fontSize: 13,
              color: '#8B6914',
              fontWeight: 600,
              textDecoration: 'none',
              fontFamily: 'Inter, system-ui, sans-serif',
            }}>
              {t('landing.logos_strip.view_index')}
            </Link>
          </div>
        </div>
      </section>

      {/* ACTIVITATE PUBLICA — dovada ca scanarile sunt reale si recente.
          Widgetul de mini-scan a plecat de aici in hero; ramanea al doilea
          exemplar pe aceeasi pagina, cu acelasi state local, si al doilea loc
          in care userul putea porni acelasi lucru. */}
      <section style={{
        padding: 'clamp(40px, 6vw, 64px) clamp(16px, 5vw, 48px)',
        background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 50%, #FFFBEB 100%)',
        borderTop: '1px solid rgba(212,175,55,0.15)',
        borderBottom: '1px solid rgba(212,175,55,0.15)',
      }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <PublicActivityFeed limit={6} />
        </div>
      </section>

      {/* CUM FUNCTIONEAZA — 3 pasi */}
      <section style={{ padding: 'clamp(48px, 8vw, 80px) clamp(16px, 5vw, 48px)', background: '#FFFFFF' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(40px, 6vw, 64px)' }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#8B6914', textTransform: 'uppercase', letterSpacing: 3, marginBottom: 12, fontFamily: 'DM Mono, monospace' }}>
              {t('landing.how_it_works.eyebrow')}
            </p>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 700, color: '#0A1428', margin: 0 }}>
              {t('landing.how_it_works.title')}
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 'clamp(20px, 3vw, 32px)' }}>
            {t('landing.how_it_works.steps', { returnObjects: true }).map(step => (
              <div key={step.num} style={{
                background: '#FAFAFA',
                padding: 'clamp(24px, 3vw, 32px)',
                borderRadius: 16,
                border: '1px solid #F3F4F6',
                position: 'relative',
              }}>
                <div style={{
                  position: 'absolute',
                  top: -16,
                  left: 24,
                  width: 36,
                  height: 36,
                  background: 'linear-gradient(135deg, #D4AF37 0%, #B8972E 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#0A1428',
                  fontFamily: 'Playfair Display, serif',
                  fontSize: 18,
                  fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(212,175,55,0.3)',
                }}>
                  {step.num}
                </div>
                <div style={{ fontSize: 40, marginBottom: 16, marginTop: 8 }}>{step.icon}</div>
                <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 22, fontWeight: 700, color: '#0A1428', marginBottom: 12 }}>
                  {step.title}
                </h3>
                <p style={{ fontSize: 15, color: '#4B5563', lineHeight: 1.6, margin: 0, fontFamily: 'Inter, system-ui, sans-serif' }}>
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MONITORIZARE CONTINUA — diferentiatorul fata de un raport one-off.
          Sta imediat dupa "Cum functioneaza" fiindca raspunde la intrebarea pe
          care o lasa deschisa pasul 3 ("am raportul, si mai departe?").
          Cifrele sunt cele reale din backend, nu marketing: cron-ul
          `daily-brand-scoring` ruleaza la 04:00 Europe/Bucharest
          (core/scan_targets.py), daily_digest_service trimite la 08:00, iar
          score_change_alert are pragul SCORE_CHANGE_THRESHOLD = 5 puncte.
          Daca vreuna se schimba in backend, textul de aici trebuie schimbat
          odata cu ea. Nota de subsol separa explicit cadenta contului (zilnic,
          Professional) de cea a indexului public (saptamanal) — altfel intram
          din nou in supra-claim de tip "290+ branduri saptamanal".
          2026-08-15 — audit al textului fata de cod. Ce NU scrie aici, fiindca
          nu exista: (a) Slack — zero implementare in backend, e doar o
          promisiune de vanzare pe planul Custom; (b) diff de surse citate
          intre scanari — SourceXRay arata sursele scanului curent, link_health
          verifica daca linkurile traiesc, dar nimeni nu compara cu ieri;
          (c) delta pe cele 6 axe ca sa fie *trimisa* — alerta compara un
          singur numar compozit (total_score / health_score), iar digestul
          compara scorul + semnalele negative, deci axele apar ca istoric in
          aplicatie, nu ca notificare. Docstring-ul din daily_digest_service.py
          promite si "Top competitor move" — codul nu-l calculeaza, movers sunt
          brandurile PROPRII ale userului. Inainte sa cresti textul de aici,
          verifica in cod ca feature-ul chiar exista. */}
      <section style={{ padding: 'clamp(48px, 8vw, 80px) clamp(16px, 5vw, 48px)', background: '#0A1428' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(36px, 5vw, 56px)' }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#D4AF37', textTransform: 'uppercase', letterSpacing: 3, marginBottom: 12, fontFamily: 'DM Mono, monospace' }}>
              {t('landing.monitoring.eyebrow')}
            </p>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 700, color: '#FFFFFF', margin: '0 0 16px' }}>
              {t('landing.monitoring.title')}
            </h2>
            <p style={{ fontSize: 'clamp(15px, 1.5vw, 17px)', color: '#D1D5DB', lineHeight: 1.6, margin: '0 auto', maxWidth: 720, fontFamily: 'Inter, system-ui, sans-serif' }}>
              {t('landing.monitoring.subtitle')}
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 'clamp(20px, 3vw, 28px)' }}>
            {t('landing.monitoring.items', { returnObjects: true }).map(item => (
              <div key={item.num} style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(212,175,55,0.25)',
                borderRadius: 16,
                padding: 'clamp(24px, 3vw, 32px)',
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 16 }}>
                  <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 28, fontWeight: 700, color: '#D4AF37', lineHeight: 1 }}>
                    {item.num}
                  </span>
                  <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: '#9CA3AF' }}>
                    {item.label}
                  </span>
                </div>
                <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 20, fontWeight: 700, color: '#FFFFFF', margin: '0 0 12px', lineHeight: 1.3 }}>
                  {item.title}
                </h3>
                <p style={{ fontSize: 15, color: '#D1D5DB', lineHeight: 1.65, margin: 0, fontFamily: 'Inter, system-ui, sans-serif' }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          <p style={{ textAlign: 'center', fontSize: 13, color: '#9CA3AF', lineHeight: 1.6, margin: 'clamp(28px, 4vw, 40px) auto 0', maxWidth: 760, fontFamily: 'Inter, system-ui, sans-serif' }}>
            {t('landing.monitoring.note')}{' '}
            <Link to="/preturi" style={{ color: '#D4AF37', textDecoration: 'underline', fontWeight: 600 }}>
              {t('landing.monitoring.note_link')}
            </Link>
          </p>
        </div>
      </section>

      {/* DATE LIVE DIN INDEX — dovada sociala verificabila, in locul
          testimonialelor anonime (care nu conving un cumparator B2B) */}
      <IndexHighlights />

      {/* TRUST BADGES — anxiety relief */}
      <section style={{ padding: 'clamp(32px, 5vw, 48px) clamp(16px, 5vw, 48px)', background: '#FFFFFF', borderTop: '1px solid #F3F4F6' }}>
        <div style={{ maxWidth: '1180px', margin: '0 auto' }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 3, marginBottom: 20, fontFamily: 'DM Mono, monospace', textAlign: 'center' }}>
            {t('landing.trust_badges.eyebrow')}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'clamp(16px, 2vw, 24px)', alignItems: 'stretch' }}>
            {t('landing.trust_badges.items', { returnObjects: true }).map((badge, i) => (
              <div key={i} style={{
                textAlign: 'center',
                padding: '16px 12px',
                background: '#FAFAFA',
                borderRadius: 12,
                border: '1px solid #F3F4F6',
              }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>{badge.icon}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0A1428', marginBottom: 4, fontFamily: 'Inter, system-ui, sans-serif' }}>
                  {badge.title}
                </div>
                <div style={{ fontSize: 11, color: '#6B7280', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  {badge.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARATIE — Manual vs Private Brands */}
      <section style={{ padding: 'clamp(48px, 8vw, 80px) clamp(16px, 5vw, 48px)', background: '#0A1428', color: '#FFFFFF' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(32px, 5vw, 48px)' }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#F5C842', textTransform: 'uppercase', letterSpacing: 3, marginBottom: 12, fontFamily: 'DM Mono, monospace' }}>
              {t('landing.comparison_table.eyebrow')}
            </p>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 700, color: '#FFFFFF', margin: 0 }}>
              {t('landing.comparison_table.title')}
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'clamp(20px, 3vw, 32px)' }}>
            {/* Coloana stanga - Manual */}
            <div style={{
              background: '#1C1828',
              padding: 'clamp(24px, 3vw, 32px)',
              borderRadius: 16,
              border: '1px solid rgba(239,68,68,0.3)',
            }}>
              <div style={{ fontSize: 28, marginBottom: 12 }}>😩</div>
              <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 22, fontWeight: 700, color: '#FFFFFF', marginBottom: 16 }}>
                {t('landing.comparison_table.manual_title')}
              </h3>
              <div style={{ fontSize: 15, color: '#D1D5DB', lineHeight: 1.8, fontFamily: 'Inter, system-ui, sans-serif' }}>
                {t('landing.comparison_table.manual_items', { returnObjects: true }).map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </div>
              <div style={{ marginTop: 24, padding: 16, background: '#2D1828', borderRadius: 10 }}>
                <div style={{ fontSize: 12, color: '#FCA5A5', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 4 }}>{t('landing.comparison_table.monthly_cost_label')}</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#FFFFFF', fontFamily: 'Playfair Display, serif' }}>{t('landing.comparison_table.manual_cost_value')}</div>
                <div style={{ fontSize: 13, color: '#D1D5DB', marginTop: 4 }}>{t('landing.comparison_table.manual_cost_caption')}</div>
              </div>
            </div>

            {/* Coloana dreapta - Private Brands */}
            <div style={{
              background: '#22272A',
              padding: 'clamp(24px, 3vw, 32px)',
              borderRadius: 16,
              border: '1px solid rgba(212,175,55,0.4)',
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute',
                top: -12,
                right: 24,
                background: '#D4AF37',
                color: '#0A1428',
                padding: '4px 12px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                fontFamily: 'DM Mono, monospace',
                letterSpacing: 1,
              }}>
                {t('landing.comparison_table.recommended_badge')}
              </div>
              <div style={{ fontSize: 28, marginBottom: 12 }}>🚀</div>
              <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 22, fontWeight: 700, color: '#FFFFFF', marginBottom: 16 }}>
                {t('landing.comparison_table.pb_title')}
              </h3>
              <div style={{ fontSize: 15, color: '#FCD34D', lineHeight: 1.8, fontFamily: 'Inter, system-ui, sans-serif' }}>
                {t('landing.comparison_table.pb_items', { returnObjects: true }).map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </div>
              <div style={{ marginTop: 24, padding: 16, background: '#32302B', borderRadius: 10 }}>
                <div style={{ fontSize: 12, color: '#FCD34D', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 4 }}>{t('landing.comparison_table.monthly_cost_label')}</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#FFFFFF', fontFamily: 'Playfair Display, serif' }}>{PROFESSIONAL_PRICE.display_excl_vat}</div>
                <div style={{ fontSize: 13, color: '#FCD34D', marginTop: 4 }}>{t('landing.comparison_table.pb_cost_caption')}</div>
              </div>
            </div>
          </div>

          {/* Economia = costul manual afisat mai sus MINUS pretul real al pachetului,
              nu o cifra scrisa in traducere: la orice schimbare de pret in tabela,
              o cifra fixa ar contrazice chiar coloana de deasupra ei. Textul RO/EN
              trebuie sa foloseasca {{savings}}; daca inca nu o face, valoarea e
              ignorata, nu afisata gresit. */}
          <div style={{ marginTop: 32, textAlign: 'center', padding: 20, background: '#1A2438', borderRadius: 12 }}>
            <span style={{ fontSize: 16, color: '#FCD34D', fontFamily: 'DM Mono, monospace' }}>
              <Trans
                i18nKey="landing.comparison_table.savings_line"
                values={{ savings: monthlySavings }}
                components={{ 1: <strong style={{ color: '#FFFFFF' }} />, 3: <strong style={{ color: '#FFFFFF' }} /> }}
              />
            </span>
          </div>
        </div>
      </section>

      <section style={{ padding: '0 48px 80px', background: '#FFFFFF' }}>
        <div style={{ maxWidth: '880px', margin: '0 auto' }}>

          {/* BENEFITS */}
          <div style={{ background: '#F3F4F6', padding: '40px', borderRadius: '12px', marginBottom: '48px', textAlign: 'left' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              {t('landing.benefits.items', { returnObjects: true }).map((b, i) => (
                <div key={i} style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ color: '#D4AF37', fontSize: '20px', fontWeight: '700' }}>✓</div>
                  <div>
                    <p style={{ fontWeight: '600', color: '#0A1428', marginBottom: '4px' }}>{b.title}</p>
                    <p style={{ fontSize: '14px', color: '#4B5563' }}>{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hero-cta-row" style={{ display: 'inline-flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Link to="/register" className="hero-cta-btn" style={{
              display: 'inline-block',
              padding: '16px 48px',
              background: '#D4AF37',
              color: '#0A1428',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: '700',
              fontSize: '16px',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              border: '2px solid #D4AF37',
              textAlign: 'center',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#0A1428';
              e.target.style.color = '#D4AF37';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#D4AF37';
              e.target.style.color = '#0A1428';
            }}
            >
              {t('landing.benefits.cta_register')}
            </Link>

            {/* Login = actiune secundara; un singur CTA primar (register) per ecran */}
            <Link to="/login" className="hero-cta-btn" style={{
              display: 'inline-block',
              padding: '16px 48px',
              background: 'transparent',
              color: '#0A1428',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: '700',
              fontSize: '16px',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              border: '2px solid #0A1428',
              textAlign: 'center',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#0A1428';
              e.target.style.color = '#FFFFFF';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.color = '#0A1428';
            }}
            >
              {t('landing.benefits.cta_login')}
            </Link>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section style={{ padding: '96px 48px', background: '#FFFFFF' }}>
        <h2 style={{ fontSize: '40px', fontWeight: '700', marginBottom: '64px', textAlign: 'center', maxWidth: '700px', margin: '0 auto 64px' }}>
          {t('landing.why_us.title')}
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px', maxWidth: '1000px', margin: '0 auto' }}>
          <div>
            <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '16px', color: '#0A1428' }}>
              {t('landing.why_us.left_title')}
            </h3>
            <p style={{ color: '#4B5563', lineHeight: '1.8', fontSize: '16px' }}>
              {t('landing.why_us.left_body')}
            </p>
          </div>

          <div>
            <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '16px', color: '#0A1428' }}>
              {t('landing.why_us.right_title')}
            </h3>
            <p style={{ color: '#4B5563', lineHeight: '1.8', fontSize: '16px' }}>
              {t('landing.why_us.right_body')}
            </p>
          </div>
        </div>
      </section>

      {/* PRICING TEASER — preturi clare, fara friction */}
      <section style={{ padding: 'clamp(48px, 8vw, 80px) clamp(16px, 5vw, 48px)', background: '#FAFAFA' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(32px, 5vw, 48px)' }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#8B6914', textTransform: 'uppercase', letterSpacing: 3, marginBottom: 12, fontFamily: 'DM Mono, monospace' }}>
              {t('landing.pricing_preview.eyebrow')}
            </p>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 700, color: '#0A1428', margin: 0 }}>
              {t('landing.pricing_preview.title')}
            </h2>
          </div>

          {/* minmax coborat la 230px: la 4 pachete, pragul de 260px depasea latimea
              containerului si randul se rupea in 2+2, cu cardul recomandat pe randul doi. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 'clamp(20px, 3vw, 24px)' }}>
            {buildPricingTeaser(t).map((plan, i) => {
              const isDemo = plan.onDemo;
              const ctaCommonStyle = {
                display: 'block',
                padding: '14px 20px',
                background: plan.highlight ? '#D4AF37' : 'transparent',
                color: plan.highlight ? '#0A1428' : '#0A1428',
                textDecoration: 'none',
                borderRadius: 10,
                fontFamily: 'DM Mono, monospace',
                fontSize: 12,
                letterSpacing: 1.5,
                fontWeight: 700,
                textTransform: 'uppercase',
                textAlign: 'center',
                border: plan.highlight ? '2px solid #D4AF37' : '2px solid #0A1428',
                marginTop: 'auto',
              };
              return (
                <div key={i} style={{
                  background: plan.highlight ? 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)' : '#FFFFFF',
                  padding: 'clamp(24px, 3vw, 32px)',
                  borderRadius: 16,
                  border: plan.highlight ? '2px solid #D4AF37' : '1px solid #E5E7EB',
                  boxShadow: plan.highlight ? '0 12px 40px rgba(212,175,55,0.2)' : '0 4px 16px rgba(13,27,42,0.04)',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  transform: plan.highlight ? 'scale(1.02)' : 'none',
                }}>
                  {plan.highlight && (
                    <div style={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: '#0A1428',
                      color: '#D4AF37',
                      padding: '6px 16px',
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      fontFamily: 'DM Mono, monospace',
                      letterSpacing: 1,
                    }}>
                      {t('landing.pricing_preview.most_popular_badge')}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: '#8B6914', textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700, marginBottom: 8, fontFamily: 'DM Mono, monospace' }}>
                    {plan.name}
                  </div>
                  <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 16, fontFamily: 'Inter, system-ui, sans-serif' }}>
                    {plan.desc}
                  </div>
                  <div style={{ marginBottom: 24 }}>
                    <span style={{ fontSize: 'clamp(36px, 5vw, 48px)', fontWeight: 800, color: '#0A1428', fontFamily: 'Playfair Display, serif', lineHeight: 1 }}>
                      {plan.price}
                    </span>
                    {plan.unit && <span style={{ fontSize: 14, color: '#6B7280', marginLeft: 8, fontFamily: 'DM Mono, monospace' }}>{plan.unit}</span>}
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px 0', flex: 1 }}>
                    {plan.features.map((f, idx) => (
                      <li key={idx} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10, fontSize: 14, color: '#0A1428', fontFamily: 'Inter, system-ui, sans-serif' }}>
                        <span style={{ color: '#10B981', fontWeight: 700, flexShrink: 0 }}>✓</span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {isDemo ? (
                    <button
                      type="button"
                      onClick={() => openDemo('pricing-enterprise')}
                      style={{ ...ctaCommonStyle, cursor: 'pointer', font: 'inherit' }}
                    >
                      {plan.cta}
                    </button>
                  ) : (
                    <Link to={plan.ctaLink} style={ctaCommonStyle}>{plan.cta}</Link>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 32, textAlign: 'center' }}>
            <Link to="/preturi" style={{ color: '#0A1428', fontFamily: 'DM Mono, monospace', fontSize: 13, letterSpacing: 1, textDecoration: 'underline', fontWeight: 600 }}>
              {t('landing.pricing_preview.see_all_link')}
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ — rezolva obiectiile vizitatorilor inainte de CTA */}
      <section style={{ padding: 'clamp(48px, 8vw, 80px) clamp(16px, 5vw, 48px)', background: '#FFFFFF' }}>
        <div style={{ maxWidth: '820px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(32px, 5vw, 48px)' }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#8B6914', textTransform: 'uppercase', letterSpacing: 3, marginBottom: 12, fontFamily: 'DM Mono, monospace' }}>
              {t('landing.faq.eyebrow')}
            </p>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 700, color: '#0A1428', margin: 0 }}>
              {t('landing.faq.title')}
            </h2>
          </div>

          {/* Raspunsul „Cat costa?" insira pachetele, deci are nevoie de pretul
              FIECARUI pachet numit. Pana in 2026-08-18 primea doar `pro_price`,
              iar textul EN cerea si {{start_price}} → placeholder-ul aparea brut
              in pagina.
              `{start,pro}_price` pastreaza aici forma ISTORICA (sirul complet
              „1.290 RON + TVA / luna"), fiindca textul RO curent nu-si scrie
              singur unitatea: schimbarea lor pe cifra goala ar sterge „+ TVA"
              dintr-o fraza care il are pe celelalte doua pachete. Pasam si
              variantele `_excl_vat` (cifra goala), pentru textele care isi scriu
              unitatea in limba lor — i18next ignora ce nu e cerut. */}
          {t('landing.faq.items', {
            returnObjects: true,
            start_price: START_PRICE.display_per_month,
            pro_price: PROFESSIONAL_PRICE.display_per_month,
            start_price_excl_vat: START_PRICE.display_excl_vat_short,
            pro_price_excl_vat: PROFESSIONAL_PRICE.display_excl_vat_short,
          }).map((faq, i) => (
            <details key={i} style={{
              background: '#FAFAFA',
              border: '1px solid #E5E7EB',
              borderRadius: 12,
              padding: '20px 24px',
              marginBottom: 12,
              cursor: 'pointer',
            }}>
              <summary style={{
                fontFamily: 'Playfair Display, serif',
                fontSize: 'clamp(16px, 2vw, 19px)',
                fontWeight: 700,
                color: '#0A1428',
                cursor: 'pointer',
                listStyle: 'none',
                position: 'relative',
                paddingRight: 32,
              }}>
                {faq.q}
                <span style={{ position: 'absolute', right: 0, top: 0, color: '#D4AF37', fontSize: 20, fontWeight: 700 }}>+</span>
              </summary>
              <p style={{ marginTop: 16, fontSize: 15, color: '#4B5563', lineHeight: 1.7, fontFamily: 'Inter, system-ui, sans-serif', margin: '16px 0 0 0' }}>
                {faq.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: 'clamp(48px, 8vw, 96px) clamp(16px, 5vw, 48px)', textAlign: 'center', background: '#0A1428', color: '#FFFFFF' }}>
        <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: '700', marginBottom: '24px', color: '#FFFFFF', fontFamily: 'Playfair Display, serif' }}>
          {t('landing.final_cta.title')}
        </h2>
        <p style={{ fontSize: 'clamp(15px, 1.5vw, 18px)', color: '#FCD34D', marginBottom: '48px', fontWeight: '500' }}>
          {t('landing.final_cta.subtitle')}
        </p>
        <Link to="/register" style={{
          display: 'inline-block',
          padding: '16px 48px',
          background: '#D4AF37',
          color: '#0A1428',
          textDecoration: 'none',
          borderRadius: '8px',
          fontWeight: '700',
          fontSize: '16px',
          transition: 'all 0.3s ease',
          border: '2px solid #D4AF37',
        }}
        onMouseEnter={(e) => {
          e.target.style.opacity = '0.9';
        }}
        onMouseLeave={(e) => {
          e.target.style.opacity = '1';
        }}
        >
          {t('landing.final_cta.button')}
        </Link>
      </section>

      </main>
      <Footer />
    </div>
  );
}
